import { SxProps } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Link, { LinkProps as MUILinkProps } from '@mui/material/Link';
import {
  Link as ReactRouterLink,
  LinkProps as ReactRouterLinkProps,
} from 'react-router-dom';

import colors from '../../../constants/colors';

const getColorMap = (primaryMain: string, primaryDark: string) => ({
  color: {
    white: colors.csaaBlack,
    blue: colors.csaaWhite,
  },
  backgroundColor: {
    white: 'none',
    blue: primaryMain,
  },
  focusColor: {
    white: 'currentColor',
    blue: primaryDark,
  },
  focusBackgroundColor: {
    white: colors.csaaOverlayBlack,
    blue: primaryDark,
  },
  hoverBackgroundColor: {
    white: colors.blackInteractiveButtonHover,
    blue: primaryDark,
  },
});

const containedLinkStyles = (
  variantColor: 'blue' | 'white',
  primaryMain: string,
  primaryDark: string
) => {
  const colorMap = getColorMap(primaryMain, primaryDark);
  return {
    fontSize: {
      mobile: 16,
      tablet: 18,
    },
    lineHeight: '24px',
    fontWeight: 700,
    color: colorMap.color[variantColor],
    backgroundColor: colorMap.backgroundColor[variantColor],
    borderRadius: 1,
    border: variantColor === 'white' ? '2px solid currentColor' : 'none',
    height: '42px',
    width: { mobile: 1, tablet: 'auto' },
    minWidth: 'auto',
    px: 4,
    py: 1,
    textAlign: 'center',
    whiteSpace: 'nowrap',
    '&:focus': {
      color: colorMap.color[variantColor],
      border: `1px solid ${colorMap.focusColor[variantColor]}`,
      outlineOffset: '1px',
      outline: `2px solid ${colorMap.focusColor[variantColor]}`,
      backgroundColor: colorMap.focusBackgroundColor[variantColor],
    },
    '&:hover': {
      backgroundColor: colorMap.hoverBackgroundColor[variantColor],
      color: colorMap.color[variantColor],
      textDecoration: 'none',
    },
  };
};

interface StyledLinkType extends MUILinkProps {
  variantColor: 'white' | 'blue';
}

const StyledLink: React.FC<ReactRouterLinkProps & MUILinkProps> = ({
  children,
  ...rest
}) => {
  const theme = useTheme();
  return (
    <Link
      component={ReactRouterLink}
      underline="none"
      sx={{
        fontSize: {
          mobile: 16,
          tablet: 18,
        },
        fontWeight: 400,
        fontFamily: 'Cabin',
        fontKerning: 'normal',
        color: theme.palette.primary.main,
        ...rest.sx,
      }}
      {...rest}
    >
      {children}
    </Link>
  );
};

export default StyledLink;

export const LinkWithNoRouter: React.FC<MUILinkProps> = ({
  children,
  href,
  ...rest
}) => (
  <Link
    data-testid="styledLink"
    href={href}
    underline="none"
    sx={{
      color: 'primary',
      fontWeight: { mobile: 400, tablet: 700 },
      fontSize: { mobile: 13, tablet: 18 },
      ...rest.sx,
    }}
    {...rest}
  >
    {children}
  </Link>
);

export const ContainedLink: React.FC<ReactRouterLinkProps & StyledLinkType> = ({
  children,
  variantColor = 'white',
  ...rest
}) => {
  const theme = useTheme();
  const primaryMain = theme.palette.primary.main;
  const primaryDark = theme.palette.primary.dark || primaryMain;

  return (
    <Link
      {...rest}
      component={ReactRouterLink}
      underline="none"
      sx={
        {
          ...rest.sx,
          ...containedLinkStyles(variantColor, primaryMain, primaryDark),
        } as SxProps
      }
    >
      {children}
    </Link>
  );
};

export const ContainedExternalLink: React.FC<StyledLinkType> = ({
  children,
  variantColor = 'white',
  ...rest
}) => {
  const theme = useTheme();
  const primaryMain = theme.palette.primary.main;
  const primaryDark = theme.palette.primary.dark || primaryMain;

  return (
    <Link
      {...rest}
      underline="none"
      sx={
        {
          ...rest.sx,
          ...containedLinkStyles(variantColor, primaryMain, primaryDark),
        } as SxProps
      }
    >
      {children}
    </Link>
  );
};
